import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const categoryArticles = articles.filter((a) => a.category === slug);

  const schemaJson = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category ? `${category.name} - 快猫平台` : '分类页面 - 快猫平台',
    description: category?.description || '',
    url: `https://kuaimao.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '快猫平台',
      url: 'https://kuaimao.com',
    },
  };

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">分类未找到</h1>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name} - 快猫平台`}
        description={category.description}
        keywords={`快猫,${category.name},快猫平台,短视频创作,内容变现`}
        canonicalUrl={`https://kuaimao.com/category/${slug}`}
        schemaJson={schemaJson}
      />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="px-4 md:px-6 py-10 md:py-14">
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className={`${category.icon} text-lg`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {category.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-sm text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}